import React from 'react';
import { Dropdown } from '@components/RingilComponents/SafeDropdown';
import { GlobalOrdersRequest } from '@api/ringil-api.v2.tsx';
import COHeadline from '@components/framework/headlines/COHeadline.tsx';
import { Field } from '@components/framework/formfield/Field.tsx';
import RingilButton from '@components/ringil3/Buttons/RingilButton';
import SvgDelete from '@icons/delete-trash.svg?react';
import SvgLoadDown from '@icons/LoadDown.tsx';
import useTranslationLgs from '@hooks/i18n/useTranslation';
import { toTestIdSlug } from '@utils/testUtils';
import { ExpeditionLocality } from '@app/pages/expedition/create/types.ts';
import { findLoadingIndex, issuesForOrder, RouteOrderIssue, unloadingCandidateIndexes } from '@app/pages/expedition/create/routeRules.ts';
import OrderIssueMessage from '@app/pages/expedition/create/components/OrderIssueMessage.tsx';
import MoveOrderDropdownButton from '@app/pages/expedition/create/components/MoveOrderDropdownButton.tsx';

interface ComponentProps {
  locIndex: number;
  location: ExpeditionLocality;
  locations: ExpeditionLocality[];
  canDelete: boolean;
  onDelete: () => void;
  /** Orders loaded before this stop, i.e. everything it could unload. */
  unloadableOrders: GlobalOrdersRequest[];
  onUnloadOrder: (locIndex: number, orderId: string) => void;
  orderIssues: RouteOrderIssue[];
  dataTestIdPrefix?: string;
}

/** The orders a stop unloads, plus the picker for adding another one. */
const UnloadingSection: React.FC<ComponentProps> = ({
                                                      locIndex,
                                                      location,
                                                      locations,
                                                      canDelete,
                                                      onDelete,
                                                      unloadableOrders,
                                                      onUnloadOrder,
                                                      orderIssues,
                                                      dataTestIdPrefix,
                                                    }) => {
  const { tr } = useTranslationLgs();

  const assignedIds = location.unloadingOrderIds ?? [];

  // Only orders picked up earlier can be offered, but everything already assigned is *shown* —
  // including assignments that stopped making sense after a stop moved. Hiding them would leave the
  // route quietly broken with nothing on screen to correct.
  const orderById = new Map(
    locations
      .flatMap(loc => loc.orders || [])
      .filter(order => order != null)
      .map(order => [order.id, order]),
  );
  const remainingOrders = unloadableOrders.filter(order => !assignedIds.includes(order.id));
  // Listed in loading order rather than in the order they were assigned; the ones with no valid
  // loading stop before this one come last.
  const validAssignedIds = unloadableOrders.map(order => order.id).filter(id => assignedIds.includes(id));
  const displayedIds = [...validAssignedIds, ...assignedIds.filter(id => !validAssignedIds.includes(id))];

  return (
    <div className={'flex flex-col gap-4'}>
      <div className={'flex flex-row justify-between items-center'}>
        <COHeadline variant={'h4'} title={tr('CreateExpedition.vylozitObjednavky', 'Unload orders')} icon={<SvgLoadDown />} />
        {canDelete && (
          <RingilButton
            mode={'link'}
            size={'small'}
            icon={<SvgDelete />}
            tooltip={tr('CreateExpedition.removeUnloadingTooltip', 'Remove unloading from this stop')}
            onClick={onDelete}
          />
        )}
      </div>

      {displayedIds?.map(orderId => {
        const order = orderById.get(orderId);
        const orderName = order?.orderName || tr('CreateExpedition.routeIssueUnnamedOrder', 'Unnamed order');
        const issues = issuesForOrder(orderIssues, orderId);

        // Anywhere after the stop the order is loaded at, except where it already is. The last stop
        // is no exception: from there an order can still be pulled back to an earlier one.
        const candidateIndexes = unloadingCandidateIndexes(locations, findLoadingIndex(locations, orderId), locIndex);

        return (
          <Field key={orderId} label={tr('CreateExpedition.unloadOrderFieldLabel', 'Unload order')}>
            <div className={'flex flex-col gap-1'}>
              <div className={'flex flex-row items-center gap-2'}>
                <div
                  className={`flex h-[40px] flex-1 items-center rounded px-3 text-content-secondary border border-solid ${issues.length > 0 ? 'bg-complementary-errorLight border-complementary-error' : 'bg-base-tertiary border-content-quaternary'}`}
                  data-testid={dataTestIdPrefix ? `${dataTestIdPrefix}-orderReference-${toTestIdSlug(orderName)}` : undefined}
                >
                  {orderName}
                </div>
                <MoveOrderDropdownButton
                  locations={locations}
                  candidateIndexes={candidateIndexes}
                  onMove={targetLocIndex => onUnloadOrder(targetLocIndex, orderId)}
                  dataTestId={dataTestIdPrefix ? `${dataTestIdPrefix}-moveUnloading` : undefined}
                />
              </div>
              {/* Spelled out under the row it belongs to, rather than hidden behind a hover. */}
              <OrderIssueMessage
                issues={issues}
                action={
                  <MoveOrderDropdownButton
                    locations={locations}
                    candidateIndexes={candidateIndexes}
                    onMove={targetLocIndex => onUnloadOrder(targetLocIndex, orderId)}
                    label={tr('CreateExpedition.moveOrderAction', 'Move')}
                    dataTestId={dataTestIdPrefix ? `${dataTestIdPrefix}-orderIssue-move` : undefined}
                  />
                }
                dataTestId={dataTestIdPrefix ? `${dataTestIdPrefix}-orderIssue` : undefined}
              />
            </div>
          </Field>
        );
      })}

      {remainingOrders && (
        <Field label={tr('CreateExpedition.unloadNextOrder', 'Unload next order')}>
          <div className="w-full" data-testid={dataTestIdPrefix ? `${dataTestIdPrefix}-orderReference` : undefined}>
            <Dropdown
              className="w-full"
              options={remainingOrders}
              optionLabel={'orderName'}
              optionValue={'id'}
              value={null}
              onChange={e => onUnloadOrder(locIndex, e.value)}
              panelClassName={dataTestIdPrefix ? `${dataTestIdPrefix}-orderReference-list` : undefined}
              itemTemplate={(option: GlobalOrdersRequest) => (
                <div data-testid={dataTestIdPrefix ? `${dataTestIdPrefix}-orderReference-id-${toTestIdSlug(option.orderName)}` : undefined}>
                  {option.orderName}
                </div>
              )}
            />
          </div>
        </Field>
      )}
    </div>
  );
};

export default UnloadingSection;